/**
 * Bill of Lading Cargo Fields Component
 *
 * The most complex component — implements a 3-level nested array hierarchy:
 *
 *   consignmentItems[]          — top-level (useFieldArray on 'consignmentItems')
 *     ├── core fields           — carrierBookingReference, description, weight, volume
 *     └── cargoItems[]          — 2nd level (useFieldArray per consignment)
 *          ├── core fields      — equipmentReference, packages, weight, volume, marks
 *          └── cargoLineItems[] — 3rd level (useFieldArray per cargo item)
 *               └── core fields — description, commodity, HSCodes, customs, licenses
 *
 * Each level is collapsible to keep the form manageable when fully populated.
 * Summary lines show key counts when collapsed (e.g., "3 cargo items").
 */

import { useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { getFieldIssues } from '../../utils/confidenceUtils';

const WEIGHT_UNITS = [
  { value: 'KGS', label: 'KGS - Kilograms' },
  { value: 'LBS', label: 'LBS - Pounds' }
];

const VOLUME_UNITS = [
  { value: 'CBM', label: 'CBM - Cubic Meters' },
  { value: 'CFT', label: 'CFT - Cubic Feet' }
];

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function SelectInline({ label, registerProps, disabled, options }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <select
        {...registerProps}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${disabled ? 'bg-gray-50 text-gray-600' : ''}`}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Level 3: Cargo Line Items
// ---------------------------------------------------------------------------

/**
 * Renders the cargoLineItems[] nested array inside a single cargo item.
 * This is the innermost (3rd) level of the hierarchy.
 */
function CargoLineItemsSection({ consignmentIndex, cargoItemIndex, control, register, isEditMode, issues }) {
  const [isOpen, setIsOpen] = useState(false);
  const basePath = `consignmentItems.${consignmentIndex}.cargoItems.${cargoItemIndex}.cargoLineItems`;
  const baseIssuePath = `consignmentItems[${consignmentIndex}].cargoItems[${cargoItemIndex}].cargoLineItems`;

  const { fields, append, remove } = useFieldArray({ control, name: basePath });

  const addLineItem = () => {
    append({
      description: '',
      commodityType: '',
      HSCodes: '',
      customsValue: null,
      customsValueCurrency: '',
      exportLicenseNumber: '',
      importLicenseNumber: ''
    });
  };

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700"
        >
          <svg className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Line Items ({fields.length})
        </button>
        {isEditMode && isOpen && (
          <Button variant="secondary" size="sm" onClick={addLineItem}>
            + Add Line Item
          </Button>
        )}
      </div>

      {!isOpen && fields.length > 0 && (
        <p className="text-xs text-gray-400 ml-5 mb-1">{fields.length} line item{fields.length !== 1 ? 's' : ''}</p>
      )}

      {isOpen && (
        <div className="space-y-2 ml-5">
          {fields.length === 0 && (
            <div className="text-center py-2 text-gray-400 text-xs bg-gray-50 rounded">
              No line items.
            </div>
          )}
          {fields.map((lineItem, liIdx) => (
            <div key={lineItem.id} className="border border-gray-100 rounded p-2 bg-white space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500">Line Item #{liIdx + 1}</span>
                {isEditMode && (
                  <button type="button" onClick={() => remove(liIdx)} className="text-red-400 hover:text-red-600 text-xs">
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {/* Description — full width textarea */}
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    {...register(`${basePath}.${liIdx}.description`)}
                    disabled={!isEditMode}
                    rows={2}
                    className={`w-full px-2 py-1.5 border rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
                    placeholder="Goods description"
                  />
                </div>

                <Input
                  label="Commodity Type"
                  {...register(`${basePath}.${liIdx}.commodityType`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${liIdx}].commodityType`)}
                  placeholder="e.g., Electronics"
                />

                {/* HS Codes — comma-separated, split on save */}
                <Input
                  label="HS Codes (comma-separated)"
                  {...register(`${basePath}.${liIdx}.HSCodes`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${liIdx}].HSCodes`)}
                  placeholder="e.g., 8471.30, 8471.41"
                />

                <Input
                  label="Customs Value"
                  type="number"
                  {...register(`${basePath}.${liIdx}.customsValue`, { valueAsNumber: true })}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${liIdx}].customsValue`)}
                />

                <Input
                  label="Customs Value Currency"
                  {...register(`${basePath}.${liIdx}.customsValueCurrency`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${liIdx}].customsValueCurrency`)}
                  placeholder="e.g., USD"
                />

                <Input
                  label="Export License Number"
                  {...register(`${basePath}.${liIdx}.exportLicenseNumber`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${liIdx}].exportLicenseNumber`)}
                />

                <Input
                  label="Import License Number"
                  {...register(`${basePath}.${liIdx}.importLicenseNumber`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${liIdx}].importLicenseNumber`)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Level 2: Cargo Items (inside a consignment item)
// ---------------------------------------------------------------------------

/**
 * Renders the cargoItems[] nested array inside a single consignment item.
 * This is the 2nd level of the hierarchy, itself hosting a 3rd level.
 */
function CargoItemsSection({ consignmentIndex, control, register, isEditMode, issues }) {
  const [isOpen, setIsOpen] = useState(true);
  const basePath = `consignmentItems.${consignmentIndex}.cargoItems`;
  const baseIssuePath = `consignmentItems[${consignmentIndex}].cargoItems`;

  const { fields, append, remove } = useFieldArray({ control, name: basePath });

  const addCargoItem = () => {
    append({
      equipmentReference: '',
      numberOfPackages: null,
      packageType: '',
      packageTypeCode: '',
      marksAndNumbers: '',
      weight: null,
      weightUnit: 'KGS',
      volume: null,
      volumeUnit: 'CBM',
      cargoLineItems: []
    });
  };

  // Build a summary string for the collapsed state
  const totalPackages = fields.reduce((sum, f) => sum + (f.numberOfPackages || 0), 0);
  const summary = fields.length > 0
    ? `${fields.length} cargo item${fields.length !== 1 ? 's' : ''}${totalPackages > 0 ? `, ${totalPackages} packages` : ''}`
    : 'No cargo items';

  return (
    <div className="border-t pt-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800"
        >
          <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          Cargo Items ({fields.length})
        </button>
        {isEditMode && isOpen && (
          <Button variant="secondary" size="sm" onClick={addCargoItem}>
            + Add Cargo Item
          </Button>
        )}
      </div>

      {/* Collapsed summary */}
      {!isOpen && (
        <p className="text-xs text-gray-400 ml-5 mb-1">{summary}</p>
      )}

      {isOpen && (
        <div className="space-y-3 ml-4">
          {fields.length === 0 && (
            <div className="text-center py-2 text-gray-400 text-sm bg-gray-50 rounded">
              No cargo items. Click "+ Add Cargo Item" to add.
            </div>
          )}

          {fields.map((cargoItem, ciIdx) => (
            <div key={cargoItem.id} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Cargo Item #{ciIdx + 1}</span>
                {isEditMode && (
                  <button type="button" onClick={() => remove(ciIdx)} className="text-red-500 hover:text-red-700 text-xs">
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                <Input
                  label="Equipment Reference"
                  {...register(`${basePath}.${ciIdx}.equipmentReference`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${ciIdx}].equipmentReference`)}
                  placeholder="Container number"
                />

                <Input
                  label="Number of Packages"
                  type="number"
                  {...register(`${basePath}.${ciIdx}.numberOfPackages`, { valueAsNumber: true })}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${ciIdx}].numberOfPackages`)}
                />

                <Input
                  label="Package Type"
                  {...register(`${basePath}.${ciIdx}.packageType`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${ciIdx}].packageType`)}
                  placeholder="e.g., Carton, Pallet"
                />

                <Input
                  label="Package Type Code"
                  {...register(`${basePath}.${ciIdx}.packageTypeCode`)}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${ciIdx}].packageTypeCode`)}
                  placeholder="e.g., CT"
                />

                <Input
                  label="Weight"
                  type="number"
                  {...register(`${basePath}.${ciIdx}.weight`, { valueAsNumber: true })}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${ciIdx}].weight`)}
                />

                <SelectInline
                  label="Weight Unit"
                  registerProps={register(`${basePath}.${ciIdx}.weightUnit`)}
                  disabled={!isEditMode}
                  options={WEIGHT_UNITS}
                />

                <Input
                  label="Volume"
                  type="number"
                  {...register(`${basePath}.${ciIdx}.volume`, { valueAsNumber: true })}
                  disabled={!isEditMode}
                  issues={getFieldIssues(issues, `${baseIssuePath}[${ciIdx}].volume`)}
                />

                <SelectInline
                  label="Volume Unit"
                  registerProps={register(`${basePath}.${ciIdx}.volumeUnit`)}
                  disabled={!isEditMode}
                  options={VOLUME_UNITS}
                />

                {/* Marks and Numbers — full-width textarea */}
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marks and Numbers</label>
                  <textarea
                    {...register(`${basePath}.${ciIdx}.marksAndNumbers`)}
                    disabled={!isEditMode}
                    rows={2}
                    className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
                    placeholder="Shipping marks and numbers"
                  />
                </div>
              </div>

              {/* Level 3: Cargo Line Items */}
              <CargoLineItemsSection
                consignmentIndex={consignmentIndex}
                cargoItemIndex={ciIdx}
                control={control}
                register={register}
                isEditMode={isEditMode}
                issues={issues}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Level 1: Consignment Items (top level)
// ---------------------------------------------------------------------------

/**
 * BLCargoFields Component
 *
 * Top-level cargo section rendering consignmentItems[] dynamic array.
 * Each consignment hosts cargoItems[], which each host cargoLineItems[].
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {Function} props.watch - react-hook-form watch
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 */
function BLCargoFields({ control, register, watch, isEditMode, issues = [] }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'consignmentItems'
  });

  const addConsignment = () => {
    append({
      carrierBookingReference: '',
      descriptionOfGoods: '',
      weight: null,
      weightUnit: 'KGS',
      volume: null,
      volumeUnit: 'CBM',
      cargoItems: []
    });
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">Cargo</h3>
        {isEditMode && (
          <Button variant="secondary" size="sm" onClick={addConsignment}>
            + Add Consignment Item
          </Button>
        )}
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No cargo added yet. Click "Add Consignment Item" to specify goods.
        </div>
      )}

      {/* Consignment items — Level 1 */}
      {fields.map((field, index) => (
        <div key={field.id} className="border border-gray-300 rounded-lg p-4 space-y-3 bg-gray-50">
          {/* Consignment header */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">Consignment Item #{index + 1}</span>
            {isEditMode && (
              <button type="button" onClick={() => remove(index)} className="text-red-500 hover:text-red-700 text-sm">
                Remove
              </button>
            )}
          </div>

          {/* Consignment core fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <Input
              label="Carrier Booking Reference"
              {...register(`consignmentItems.${index}.carrierBookingReference`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `consignmentItems[${index}].carrierBookingReference`)}
            />

            {/* Description of Goods — spans two columns */}
            <div className="md:col-span-2">
              <Input
                label="Description of Goods"
                {...register(`consignmentItems.${index}.descriptionOfGoods`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `consignmentItems[${index}].descriptionOfGoods`)}
                placeholder="General description of cargo"
              />
            </div>

            <Input
              label="Weight"
              type="number"
              {...register(`consignmentItems.${index}.weight`, { valueAsNumber: true })}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `consignmentItems[${index}].weight`)}
            />

            <SelectInline
              label="Weight Unit"
              registerProps={register(`consignmentItems.${index}.weightUnit`)}
              disabled={!isEditMode}
              options={WEIGHT_UNITS}
            />

            <Input
              label="Volume"
              type="number"
              {...register(`consignmentItems.${index}.volume`, { valueAsNumber: true })}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `consignmentItems[${index}].volume`)}
            />

            <SelectInline
              label="Volume Unit"
              registerProps={register(`consignmentItems.${index}.volumeUnit`)}
              disabled={!isEditMode}
              options={VOLUME_UNITS}
            />
          </div>

          {/* Level 2: Cargo Items (collapsible) */}
          <CargoItemsSection
            consignmentIndex={index}
            control={control}
            register={register}
            isEditMode={isEditMode}
            issues={issues}
          />
        </div>
      ))}
    </div>
  );
}

export default BLCargoFields;
